import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { FadeUp } from "./Reveal";
import { IMAGES } from "../data/content";

/**
 * A full-bleed photograph "belt" that sits between the hero and the marquee.
 * Serves as the visual bridge from the individual freshman moment into
 * the wider community narrative — with a soft italic quote overlaid.
 *
 * Readability fix (2026-07-19f): the earlier version used top+bottom
 * third-height scrims, but the quote was centered vertically — it sat in
 * the middle where there was no scrim, so the sand text got lost against
 * the walking students' bright clothing + sky. Replaced with a left-heavy
 * horizontal ink gradient (dark on the text side, transparent on the
 * photo side) plus a text-shadow on the quote lines so glyphs stay legible
 * even against high-contrast photo patches.
 */
export const CommunityBelt = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden bg-ink"
      data-testid="community-belt"
    >
      <motion.img
        style={{ y }}
        src={IMAGES.walking}
        alt="Four students walking together on a campus path, laughing"
        className="h-[34vh] max-h-[380px] min-h-[300px] w-full object-cover object-center"
      />

      {/* Left-heavy ink veil — ~92% ink from 0-35% of the width (where the
          text sits), transitioning to ~15% ink at 100% so the walking
          students on the right remain visible. This is the load-bearing
          readability fix — Tailwind's default gradient stops (0/50/100)
          left the text-area under-darkened, so we use custom stops. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(35,35,32,0.92) 0%, rgba(35,35,32,0.85) 35%, rgba(35,35,32,0.45) 65%, rgba(35,35,32,0.12) 100%)",
        }}
      />
      {/* Soft top + bottom tapers so the belt's edges dissolve into the
          surrounding sections instead of hard-cutting. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-ink/40 to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink/40 to-transparent"
      />

      {/* Quote overlay — the sand text now sits over the darkest part of
          the gradient. Text-shadow adds extra glyph edge definition for
          the rare tricky pixels where the underlying photo is still bright. */}
      <div className="absolute inset-0 flex items-center">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
          <FadeUp className="max-w-2xl">
            <p
              data-testid="community-belt-quote"
              className="font-serif text-3xl leading-[1.15] text-sand"
              style={{ textShadow: "0 2px 14px rgba(0,0,0,0.6)" }}
            >
              &ldquo;Nobody makes it through college alone. My Rich Aunt is the family you didn't know you had.&rdquo;
            </p>
            <p
              className="mt-5 text-[13px] italic text-sand/85"
              style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
            >
              &mdash; The team behind My Rich Aunt
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
